import {
  Session,
  TabStatus,
  useCountParticipationByStatus,
  useIsAdmin,
} from '@app/api';
import {
  Navigate,
  Route,
  Routes,
  useMatch,
  useNavigate,
  useResolvedPath,
} from 'react-router-dom';
import { TableView } from './TableView';
import {
  getFirstVisibleTab,
  useStudyTypeDetailTabs,
} from './hooks/participationDetailTabs';
import { CenteredLoader } from 'components';
import { createUseStyles } from 'react-jss';
import { Theme } from '@app/theme';
import { MergedStatusTab } from './components/MergedStatusTab';
import { useCallback } from 'react';

interface Props {
  session: Session;
  canSeeQuotas?: boolean;
}

const useStyles = createUseStyles((theme: Theme) => ({
  container: {
    padding: theme.marginBase * 5,
  },
  tabs: {
    display: 'flex',
    gap: theme.marginBase * 2,
    flexWrap: 'wrap',
  },
}));

export const ParticipationsDetails = ({ session, canSeeQuotas }: Props) => {
  const classes = useStyles();
  const tabs = useStudyTypeDetailTabs(session.studyType, !!canSeeQuotas);
  const isAdmin = useIsAdmin();
  const { data: countByStatus } = useCountParticipationByStatus(session.id);
  const navigate = useNavigate();

  const { pathname } = useResolvedPath('tab/:tab');
  const match = useMatch<'tab', string>(`${pathname}/*`);
  const selectedTab = match?.params.tab;

  const initialTab = getFirstVisibleTab(tabs, isAdmin, countByStatus);

  const onTabChanged = useCallback(
    (tab: TabStatus) => {
      navigate('tab/' + tab);
    },
    [navigate],
  );

  return (
    <div className={classes.container}>
      <nav className={classes.tabs}>
        {Object.entries(tabs).map(
          ([key, { label, statusList, adminOnly, visibleOnDataOnly }]) =>
            (!adminOnly || !!isAdmin) && (
              <MergedStatusTab
                key={key}
                name={key as TabStatus}
                label={label}
                statusList={statusList}
                countByStatus={countByStatus}
                onClick={onTabChanged}
                selected={selectedTab === key}
                visibleOnDataOnly={visibleOnDataOnly}
              />
            ),
        )}
      </nav>

      <Routes>
        <Route
          path="tab/:tab/*"
          element={<TableView session={session} canSeeQuotas={canSeeQuotas} />}
        />
        <Route
          path="*"
          element={
            countByStatus ? (
              <Navigate to={`tab/${initialTab}`} />
            ) : (
              <CenteredLoader />
            )
          }
        />
      </Routes>
    </div>
  );
};
